/**
 * Derived-stat resolution.
 *
 * Canonical order: all `add` summed, then all `mul_add` summed into one
 * (1 + sum) multiplier, then `mul` applied. Upgrades are visited by array
 * index, never by object-key iteration — with a Record of stacks, the
 * iteration order of that record would otherwise leak into the result and
 * two players with the same upgrades in a different pick order would get
 * different stats.
 */
import type { StatName } from './config.js';
import type { RunState } from './state.js';

const STAT_KEYS: readonly StatName[] = [
  'move_speed', 'damage_per_hit', 'fire_rate',
  'projectiles_per_shot', 'pierce', 'max_hp', 'pickup_radius',
];

export function recomputeStats(s: RunState): void {
  const cfg = s.config;
  const w = cfg.weapons[s.weaponIdx]!;
  const r = cfg.rules;

  const base: Record<StatName, number> = {
    move_speed: r.moveSpeed,
    damage_per_hit: w.damagePerHit,
    fire_rate: 1,
    projectiles_per_shot: w.projectilesPerShot,
    pierce: w.pierce,
    max_hp: r.squadUnitHp,
    pickup_radius: r.pickupRadius,
  };

  for (const key of STAT_KEYS) {
    let add = 0;
    let mulAdd = 0;
    let mul = 1;

    for (let i = 0; i < cfg.upgrades.length; i++) {
      const stacks = s.upgradeStacks[i];
      if (stacks === 0) continue;
      const u = cfg.upgrades[i]!;
      if (u.stat !== key) continue;
      if (u.op === 'add') add += u.value * stacks;
      else if (u.op === 'mul_add') mulAdd += u.value * stacks;
      // Repeated multiplication, not Math.pow: pow is implementation-
      // approximated and differs between JS engines (AUDIT A-03).
      else for (let k = 0; k < stacks; k++) mul *= u.value;
    }

    const value = (base[key] + add) * (1 + mulAdd) * mul;
    switch (key) {
      case 'move_speed': s.statMoveSpeed = value; break;
      case 'damage_per_hit': s.statDamage = value; break;
      case 'fire_rate': s.statFireRate = value; break;
      case 'projectiles_per_shot': s.statProjectiles = Math.floor(value); break;
      case 'pierce': s.statPierce = Math.floor(value); break;
      case 'max_hp': s.statMaxHp = value; break;
      case 'pickup_radius': s.statPickupRadius = value; break;
    }
  }

  // CHARACTER POWERS. Each roster character bends the whole loadout a
  // different way -- heavier rounds, faster trigger and feet, tougher
  // soldiers, extra streams. Applied here so sim, HUD and renderer all
  // read one truth, and part of init so replays are exact.
  const ch = cfg.characters[s.characterIdx];
  if (ch) {
    s.statDamage *= ch.damageMult;
    s.statFireRate *= ch.fireRateMult;
    s.statMoveSpeed *= ch.moveMult;
    s.statProjectiles += Math.round(ch.extraStreams);
  }

  // A deployed VEHICLE brings its own guns. It no longer multiplies the
  // whole curtain: with the ×mult applied to every stream at full rifle
  // reach, one tank cleared the corridor to the horizon -- measured as the
  // street emptying to ~100 bodies in every vehicle window, below the
  // reference density floor, and no config point could hold both the
  // floor and the win band. The vehicle's own streams carry the ×mult and
  // a bounded reach at the fire site instead: a tank hits hard in FRONT
  // of itself, not at the vanishing point.
  if (s.vehicleTicks > 0) {
    s.statProjectiles += Math.round(r.vehicleExtraStreams);
  }
}
